<script lang="ts">
  import Layout from "../components/layout/Layout.svelte";

  let { user = null, notifications = [] }: {
    user?: { id: string; name: string; email: string; avatarUrl?: string } | null;
    notifications?: Array<{
      id: string;
      type: "schedule" | "expense" | "invite" | "settlement" | "system";
      title: string;
      body: string;
      read: boolean;
      createdAt: string;
      actionUrl?: string;
    }>;
  } = $props();

  const typeIcon: Record<string, string> = {
    schedule: "📅",
    expense: "💰",
    invite: "✈️",
    settlement: "💸",
    system: "🔔",
  };

  const typeLabel: Record<string, string> = {
    schedule: "일정",
    expense: "경비",
    invite: "초대",
    settlement: "정산",
    system: "시스템",
  };

  let activeFilter = $state("all");
  const filters = [
    { id: "all", label: "전체" },
    { id: "schedule", label: "일정" },
    { id: "expense", label: "경비" },
    { id: "invite", label: "초대" },
    { id: "settlement", label: "정산" },
  ];

  // Mock data when no notifications provided
  const mockNotifications = [
    { id: "1", type: "schedule" as const, title: "일정 알림", body: "내일 오전 9시에 '신주쿠 교엔' 방문 일정이 있습니다.", read: false, createdAt: new Date(Date.now() - 1000 * 60 * 30).toISOString(), actionUrl: "/trips/1/schedule" },
    { id: "2", type: "invite" as const, title: "여행 초대", body: "이지원님이 '오사카 여행'에 초대했습니다.", read: false, createdAt: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString(), actionUrl: "/invite/abc123" },
    { id: "3", type: "expense" as const, title: "경비 등록", body: "박민호님이 경비 '신칸센 요금 ¥15,000'을 등록했습니다.", read: true, createdAt: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString() },
    { id: "4", type: "settlement" as const, title: "정산 완료", body: "'도쿄 여행' 정산이 완료되었습니다. 총 ₩320,000을 받아야 합니다.", read: true, createdAt: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString() },
  ];

  let displayNotifications = $derived(
    (notifications.length > 0 ? notifications : mockNotifications).filter(n =>
      activeFilter === "all" || n.type === activeFilter
    )
  );

  let unreadCount = $derived(
    (notifications.length > 0 ? notifications : mockNotifications).filter(n => !n.read).length
  );

  function formatTime(dateStr: string): string {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 60) return `${mins}분 전`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}시간 전`;
    return `${Math.floor(hours / 24)}일 전`;
  }

  async function markAllRead() {
    // TODO: API call
  }
</script>

<Layout {user}>
  <div class="max-w-2xl mx-auto px-4 py-8">
    <!-- Header -->
    <div class="flex items-center justify-between mb-6">
      <div>
        <h1 class="text-2xl font-bold" style="color: var(--text-primary);">알림</h1>
        {#if unreadCount > 0}
          <p class="text-sm mt-1" style="color: var(--text-muted);">읽지 않은 알림 {unreadCount}개</p>
        {/if}
      </div>
      {#if unreadCount > 0}
        <button
          onclick={markAllRead}
          class="text-sm font-medium px-4 py-2 rounded-xl transition-colors"
          style="background: var(--surface); color: var(--accent); border: 1px solid var(--border);"
        >
          모두 읽음
        </button>
      {/if}
    </div>

    <!-- Filter Tabs -->
    <div class="flex gap-2 mb-6 overflow-x-auto pb-1 scrollbar-hide">
      {#each filters as f}
        <button
          onclick={() => activeFilter = f.id}
          class="flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all"
          style="background: {activeFilter === f.id ? 'var(--accent)' : 'var(--surface)'}; color: {activeFilter === f.id ? '#000' : 'var(--text-secondary)'}; border: 1px solid {activeFilter === f.id ? 'transparent' : 'var(--border)'};"
        >
          {f.label}
        </button>
      {/each}
    </div>

    <!-- Notifications List -->
    {#if displayNotifications.length === 0}
      <div class="text-center py-20">
        <div class="text-4xl mb-3">🔔</div>
        <p class="font-medium" style="color: var(--text-secondary);">알림이 없습니다</p>
        <p class="text-sm mt-1" style="color: var(--text-muted);">새 알림이 오면 여기에 표시됩니다</p>
      </div>
    {:else}
      <div class="space-y-2">
        {#each displayNotifications as notif (notif.id)}
          <a
            href={notif.actionUrl || "#"}
            class="flex gap-4 p-4 rounded-2xl transition-all block"
            style="background: {notif.read ? 'var(--surface)' : 'rgba(32,178,170,0.06)'}; border: 1px solid {notif.read ? 'var(--border)' : 'rgba(32,178,170,0.15)'};"
          >
            <div
              class="w-11 h-11 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
              style="background: {notif.read ? 'rgba(255,255,255,0.04)' : 'rgba(32,178,170,0.1)'};"
            >
              {typeIcon[notif.type]}
            </div>
            <div class="flex-1 min-w-0">
              <div class="flex items-start justify-between gap-2">
                <p class="text-sm font-semibold" style="color: var(--text-primary);">{notif.title}</p>
                <div class="flex items-center gap-2 flex-shrink-0">
                  {#if !notif.read}
                    <span class="w-2 h-2 rounded-full bg-[#20B2AA] flex-shrink-0"></span>
                  {/if}
                  <span class="text-xs" style="color: var(--text-muted);">{formatTime(notif.createdAt)}</span>
                </div>
              </div>
              <p class="text-sm mt-1 leading-relaxed" style="color: var(--text-secondary);">{notif.body}</p>
              <span class="inline-block text-xs mt-2 px-2 py-0.5 rounded-full" style="background: rgba(255,255,255,0.05); color: var(--text-muted);">{typeLabel[notif.type]}</span>
            </div>
          </a>
        {/each}
      </div>
    {/if}
  </div>
</Layout>
